import { useEffect, useState, useRef } from 'react';
import { useLanguage } from '../utils/i18n';
import { QRCodeSVG } from 'qrcode.react';
import { api } from '../utils/api';
import { useNotification } from '../utils/NotificationContext';
import { useAuth } from '../utils/AuthContext';
import CustomPosterStudio from './CustomPosterStudio';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || window.location.origin;

export default function BusinessQRModal({ business, onClose, isDemo }) {
  const { session } = useAuth();
  const { t } = useLanguage();
  const { showNotification } = useNotification();
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showClassicModal, setShowClassicModal] = useState(false);
  const qrRef = useRef(null);
  const posterRef = useRef(null);

  const userEmail = (session?.user?.email || '').toLowerCase().trim();
  const isTester = 
    userEmail === 'wafiyo.co@gmail.com' ||
    userEmail.startsWith('wafiyo') ||
    business?.user_id === '92469086-4f08-4bfc-a4c9-114240935800' ||
    business?.name?.toLowerCase()?.includes('wafiyo');

  const registrationUrl = `${FRONTEND_URL}/register/${business.id}`;

  useEffect(() => {
    if (isDemo) {
      setQrDataUrl('https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=https://wafiyo.com/register/demo');
      setLoading(false);
      return;
    }
    async function fetchQR() {
      try {
        const data = await api.getBusinessQR(business.id);
        setQrDataUrl(data.qrCode);
      } catch (err) {
        showNotification(err.message, 'error');
      } finally {
        setLoading(false);
      }
    }
    fetchQR();
  }, [business.id, isDemo]);

  async function handleDownloadPoster() {
    if (!posterRef.current) return;
    
    setIsGenerating(true);
    try {
      const canvas = await html2canvas(posterRef.current, {
        scale: 2.5, // High quality 300 DPI
        backgroundColor: '#0b0f19',
        useCORS: true,
        logging: false
      });
      
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      pdf.addImage(imgData, 'PNG', 0, 0, 210, 297, undefined, 'FAST');
      const fileName = `${(business?.name || 'loyalty').toLowerCase().replace(/\s+/g, '-')}-counter-stand.pdf`;
      pdf.save(fileName);
      showNotification('PDF downloaded successfully! Ready to print.', 'success');
    } catch (error) {
      console.error('Error generating poster PDF:', error);
      showNotification('Failed to generate poster PDF', 'error');
    } finally {
      setIsGenerating(false);
    }
  }

  // If logged in as tester, show the Level 2 Customizable Studio
  if (isTester && !showClassicModal) {
    return (
      <CustomPosterStudio
        business={business}
        onClose={onClose}
        isDemo={isDemo}
        qrDataUrl={qrDataUrl}
        registrationUrl={registrationUrl}
      />
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal glass-card" style={{ maxWidth: 460, position: 'relative', overflow: 'hidden' }}>
        {/* Decorative Background Shapes */}
        <div className="bg-shape shape-1" style={{ position: 'absolute', top: '-10%', right: '-10%', width: 200, height: 200, background: 'var(--primary)', filter: 'blur(60px)', opacity: 0.1, borderRadius: '50%', zIndex: 0 }}></div>
        <div className="bg-shape shape-2" style={{ position: 'absolute', bottom: '-10%', left: '-10%', width: 250, height: 250, background: 'var(--accent)', filter: 'blur(80px)', opacity: 0.15, borderRadius: '50%', zIndex: 0 }}></div>

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div className="modal-header">
            <div>
              <h2 style={{ fontSize: 24, fontWeight: 800 }}>{t('business_signup_qr')}</h2>
              {isTester && (
                <button
                  onClick={() => setShowClassicModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--primary, #3b82f6)',
                    fontSize: 12,
                    cursor: 'pointer',
                    padding: 0,
                    textDecoration: 'underline'
                  }}
                >
                  ✨ Switch to Custom Poster Studio
                </button>
              )}
            </div>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>

          <div style={{ background: 'var(--surface)', padding: '12px 16px', borderRadius: 12, marginBottom: 16, border: '1px solid var(--border)' }}>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>
              📱 <strong>{t('qr_modal_tip_title') || 'How to test:'}</strong> {t('qr_modal_tip_desc') || 'On mobile, tap "Open on this phone". On computer, scan the QR code with your smartphone camera.'}
            </p>
          </div>

          {loading && <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '40px 0' }}>{t('generating_qr')}</p>}

          {qrDataUrl && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ background: '#fff', padding: 16, borderRadius: 20, display: 'inline-block', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
                <img
                  src={qrDataUrl}
                  alt="Business registration QR"
                  style={{ width: 200, height: 200, display: 'block' }}
                />
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 12, wordBreak: 'break-all' }}>
                {registrationUrl}
              </p>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <a
                href={registrationUrl}
                target="_blank"
                rel="noreferrer"
                className="btn btn-primary"
                style={{ flex: 1, justifyContent: 'center', textDecoration: 'none' }}
              >
                <span>📱 {t('open_on_device') || 'Open on this phone'}</span>
              </a>
              <button
                className="btn btn-outline"
                onClick={() => {
                  navigator.clipboard.writeText(registrationUrl);
                  showNotification(t('link_copied') || 'Registration link copied to clipboard!', 'success');
                }}
                style={{ flexShrink: 0 }}
                title={t('copy_link') || 'Copy Link'}
              >
                📋 {t('copy') || 'Copy'}
              </button>
            </div>

            {qrDataUrl && (
              <>
                <button 
                  className="btn btn-outline btn-glow" 
                  style={{ width: '100%', justifyContent: 'center', background: 'var(--surface)' }} 
                  onClick={handleDownloadPoster}
                  disabled={isGenerating}
                >
                  📄 {isGenerating ? t('loading') : 'Download Counter Stand (PDF)'}
                </button>
                {isTester && (
                  <button
                    className="btn btn-primary"
                    style={{ width: '100%', justifyContent: 'center' }}
                    onClick={() => setShowClassicModal(false)}
                  >
                    🎨 Open Custom Poster Studio
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Hidden QR for Generation */}
        <div ref={qrRef} style={{ display: 'none' }}>
          <QRCodeSVG value={registrationUrl} size={1024} level="H" includeMargin={false} />
        </div>

        {/* Hidden Poster Template for Image Generation */}
        <div 
          ref={posterRef} 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: -9999, // Move off-screen
            width: '162mm', // C5 width
            height: '229mm', // C5 height
            padding: '40px',
            background: '#0b0f19',
            color: '#f1f5f9',
            fontFamily: "'Outfit', sans-serif",
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: -1,
            boxSizing: 'border-box'
          }}
        >
          {/* Background Shapes */}
          <div style={{
            position: 'absolute',
            width: 300, height: 300,
            background: '#3b82f6',
            borderRadius: '50%',
            filter: 'blur(80px)',
            opacity: 0.4,
            top: -100, right: -100,
            zIndex: 0
          }} />
          <div style={{
            position: 'absolute',
            width: 250, height: 250,
            background: '#8b5cf6',
            borderRadius: '50%',
            filter: 'blur(80px)',
            opacity: 0.4,
            bottom: -50, left: -80,
            zIndex: 0
          }} />

          {/* Content */}
          <div style={{ position: 'relative', zIndex: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <span style={{ 
                fontSize: 24, 
                fontWeight: 800, 
                letterSpacing: '-0.5px',
                color: '#94a3b8' // Subtle text color, no gradient/bg
              }}>Wafiyo</span>
            </div>

            <h1 style={{ 
              fontSize: 42, 
              margin: '0 0 12px 0', 
              fontWeight: 800, 
              color: '#fff',
              lineHeight: 1.1,
              letterSpacing: '-1px',
              textAlign: 'center'
            }}>{business.name}</h1>

            <p style={{ 
              fontSize: 16, 
              color: '#94a3b8', 
              maxWidth: 400,
              margin: '0 auto 32px auto',
              lineHeight: 1.4,
              textAlign: 'center'
            }}>{t('print_poster_subtitle')}</p>
            
            <div style={{ 
              background: 'white', 
              padding: 24, 
              borderRadius: 32, 
              display: 'inline-block',
              boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.5), 0 0 0 4px rgba(255, 255, 255, 0.05)',
              marginBottom: 32,
              position: 'relative'
            }}>
              {qrDataUrl && <img src={qrDataUrl} alt="QR" style={{ width: 240, height: 240, display: 'block' }} />}
            </div>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: 16, 
              width: '100%', 
              marginBottom: 32 
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <div style={{ 
                  width: 48, height: 48, 
                  background: 'rgba(255, 255, 255, 0.03)', 
                  border: '1px solid rgba(255, 255, 255, 0.08)', 
                  borderRadius: 16, 
                  display: 'flex', alignItems: 'center', justifyContent: 'center', 
                  fontSize: 20 
                }}>📸</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#94a3b8', textAlign: 'center' }}>{t('print_poster_step_1')}</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <div style={{ 
                  width: 48, height: 48, 
                  background: 'rgba(255, 255, 255, 0.03)', 
                  border: '1px solid rgba(255, 255, 255, 0.08)', 
                  borderRadius: 16, 
                  display: 'flex', alignItems: 'center', justifyContent: 'center', 
                  fontSize: 20 
                }}>📝</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#94a3b8', textAlign: 'center' }}>{t('print_poster_step_2')}</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <div style={{ 
                  width: 48, height: 48, 
                  background: 'rgba(255, 255, 255, 0.03)', 
                  border: '1px solid rgba(255, 255, 255, 0.08)', 
                  borderRadius: 16, 
                  display: 'flex', alignItems: 'center', justifyContent: 'center', 
                  fontSize: 20 
                }}>📲</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#94a3b8', textAlign: 'center' }}>{t('print_poster_step_3')}</div>
              </div>
            </div>

            <div style={{ 
              borderTop: '1px solid rgba(255, 255, 255, 0.05)', 
              paddingTop: 24, 
              width: '100%',
              textAlign: 'center'
            }}>
              <p style={{ margin: 0, fontWeight: 600, fontSize: 13, color: '#f1f5f9' }}>{t('print_poster_no_app')}</p>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8, opacity: 0.6 }}>{t('print_poster_powered_by')}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
